import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, IconButton, Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useData } from '../context/DataContext';
import { useNavigate, useLocation } from 'react-router-dom';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import WorkIcon from '@mui/icons-material/Work';
import EngineeringIcon from '@mui/icons-material/Engineering';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArticleIcon from '@mui/icons-material/Article';
import { BRAND } from '../theme/tokens';

const FI_BLUE   = '#027BFF';
const FI_ORANGE = '#F57C00';

const SIDEBAR_WIDTH_EXPANDED = 220;
const SIDEBAR_WIDTH_COLLAPSED = 56;

// v1.13 — Statements is now the parent entry point for the Bill Import
// workflow. The legacy "Bill Importer" top-level tab was removed; the
// reconciliation queue is only reachable as a child of a Statement
// (/bills/import?statement=<id>). A bare /bills/import redirects to
// /statements (see BillImportGate in App.tsx).
const NAV_TABS = [
  { label: 'Services',                path: '/services' },
  { label: 'Products',                path: '/products' },
  { label: 'Additional charges',      path: '/additional-charges' },
  { label: 'Supplier Price Books',    path: '/supplier-price-books' },
  { label: 'Jobs',                    path: '/jobs' },
  { label: 'Time & Materials',        path: '/time-and-materials' },
  { label: 'Quotes',                  path: '/quotes-list' },
  { label: 'Invoices',                path: '/invoices-list' },
  { label: 'Purchase Orders',         path: '/purchase-orders-list' },
  { label: 'Statements',              path: '/statements' },
  { label: 'Bills',                   path: '/bills' },
];

export const TopNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = NAV_TABS.findIndex(t =>
    location.pathname.startsWith(t.path) ||
    // Quote detail (/quotes/:id) maps back to the Quotes list tab.
    (t.path === '/quotes-list' && location.pathname.startsWith('/quotes')) ||
    (t.path === '/invoices-list' && location.pathname.startsWith('/invoices'))
  );

  return (
    <Box sx={{ borderBottom: '2px solid #e0e0e0', backgroundColor: '#fff' }}>
      <Tabs
        value={currentTab === -1 ? 1 : currentTab}
        onChange={(_, v) => navigate(NAV_TABS[v].path)}
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ style: { backgroundColor: FI_ORANGE, height: 3 } }}
        sx={{
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: '0.82rem',
            fontWeight: 500,
            color: '#555',
            minWidth: 'unset',
            px: 2,
          },
          '& .Mui-selected': {
            color: `${FI_ORANGE} !important`,
            fontWeight: 700,
          },
          '& .MuiTab-root:last-child': {
            color: FI_BLUE,
            fontWeight: 700,
          },
        }}
      >
        {NAV_TABS.map(t => (
          <Tab key={t.path} label={t.label} />
        ))}
      </Tabs>
    </Box>
  );
};

const SIDEBAR_ITEMS = [
  { label: 'Jobs',            path: '/jobs',                 icon: WorkIcon },
  { label: 'Time & Materials', path: '/time-and-materials',  icon: EngineeringIcon },
  { label: 'Schedule',        path: '/schedule',             icon: CalendarMonthIcon },
  { label: 'Customers',       path: null,                   icon: PeopleIcon },
  { label: 'Quotes',          path: '/quotes-list',         icon: RequestQuoteIcon },
  { label: 'Invoices',        path: '/invoices-list',       icon: ReceiptIcon },
  { label: 'Inventory',       path: '/products',            icon: InventoryIcon },
  { label: 'Purchase Orders', path: '/purchase-orders-list', icon: ShoppingCartIcon },
  { label: 'Statements',         path: '/statements',          icon: ArticleIcon },
  { label: 'Bills',              path: '/bills',               icon: ReceiptLongIcon },
  { label: 'Reports',            path: null,                   icon: AssessmentIcon },
];

export const ResetDataButton: React.FC = () => {
  const { resetData } = useData();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip title="Reset all prototype data to defaults">
        <Button
          size="small"
          startIcon={<RestartAltIcon sx={{ fontSize: 13 }} />}
          onClick={() => setOpen(true)}
          sx={{ textTransform: 'none', fontSize: '0.65rem', color: '#999', px: 1, py: 0.3, minWidth: 0,
            border: '1px solid #e0e0e0', '&:hover': { borderColor: '#f44336', color: '#f44336', backgroundColor: '#fff5f5' } }}
        >
          Reset Data
        </Button>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>Reset Prototype Data?</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.82rem', color: '#555', lineHeight: 1.6 }}>
            This will reset <strong>all bills, POs, synced line items, and parsed data</strong> back to the 12 default test scenarios.
            Any bills you created or edits you made will be lost.
          </Typography>
          <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1.5, fontSize: '0.72rem', color: '#777' }}>
            <strong>Test scenarios restored:</strong>
            <br />TC-001 · Price variance demo (PO-4575)
            <br />TC-003 · 10-item bill vs 2-item PO (PO-4570)
            <br />TC-009 · Perfect match auto-sync (PO-4580)
            <br />TC-010 · Short shipment (PO-4581)
            <br />TC-012 · Mixed mismatch (PO-4583)
            <br />+ 7 more scenarios
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', color: '#888' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { setOpen(false); resetData(); }}
            sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: '#f44336', '&:hover': { backgroundColor: '#c62828' } }}
          >
            Reset All Data
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activePath = location.pathname;
  const [collapsed, setCollapsed] = useState(false);

  const sidebarWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;

  return (
    <Box sx={{
      width: sidebarWidth,
      flexShrink: 0,
      backgroundColor: BRAND,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.2s ease-in-out',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{ 
        backgroundColor: FI_BLUE, 
        px: collapsed ? 1 : 2, 
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        minHeight: 56,
      }}>
        {!collapsed && (
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1rem', letterSpacing: 1 }}>
              FIELDINSIGHT
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem' }}>
              Northbridge Diesel & Mechanical
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.58rem', mt: 0.3, fontFamily: 'monospace', letterSpacing: 0.5 }}>
              {`v${process.env.REACT_APP_VERSION ?? '2.0.4'}`}
              {process.env.REACT_APP_GIT_SHA ? ` · ${process.env.REACT_APP_GIT_SHA}` : ''}
            </Typography>
          </Box>
        )}
        <Tooltip title={collapsed ? 'Expand menu' : 'Collapse menu'} placement="right">
          <IconButton 
            onClick={() => setCollapsed(!collapsed)}
            sx={{ 
              color: '#fff', 
              p: 0.5,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            {collapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Nav Items */}
      {SIDEBAR_ITEMS.map(item => {
        const isActive = item.path
          ? activePath.startsWith(item.path)
            || (item.path === '/products' && (activePath === '/' || activePath.startsWith('/supplier') || activePath.startsWith('/stock')))
            || (item.path === '/quotes-list' && activePath.startsWith('/quotes'))
            || (item.path === '/invoices-list' && activePath.startsWith('/invoices'))
          : false;
        const IconComponent = item.icon;
        
        return (
          <Tooltip 
            key={item.label} 
            title={collapsed ? item.label : ''} 
            placement="right"
            arrow
          >
            <Box
              onClick={() => item.path && navigate(item.path)}
              sx={{
                px: collapsed ? 1.5 : 2.5,
                py: 1.2,
                cursor: item.path ? 'pointer' : 'default',
                backgroundColor: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
                borderLeft: isActive ? '3px solid #fff' : '3px solid transparent',
                '&:hover': { backgroundColor: item.path ? 'rgba(255,255,255,0.1)' : 'transparent' },
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                justifyContent: collapsed ? 'center' : 'flex-start',
              }}
            >
              <IconComponent sx={{ 
                fontSize: 18, 
                color: isActive ? '#fff' : 'rgba(255,255,255,0.7)',
              }} />
              {!collapsed && (
                <Typography sx={{ 
                  color: '#fff', 
                  fontSize: '0.82rem', 
                  fontWeight: isActive ? 700 : 400,
                  whiteSpace: 'nowrap',
                }}>
                  {item.label}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      })}

      {/* Version footer - shown only when collapsed */}
      {collapsed && (
        <Box sx={{ mt: 'auto', px: 1, py: 1.5, textAlign: 'center' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.55rem', fontFamily: 'monospace' }}>
            {`v${process.env.REACT_APP_VERSION ?? '2.0.4'}`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
